import React from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface JobProgressOverlayProps {
  isVisible: boolean;
  progress: number; // 0-100
  jobId?: string;
  message?: string;
  errorMessage?: string;
  isComplete?: boolean;
  onCancel?: () => void;
}

/**
 * Reusable fullscreen overlay for displaying async job processing progress
 * Shows progress bar, percentage, job ID, and optional error messages
 * Fixed to cover full screen and update progress in real-time
 */
export const JobProgressOverlay: React.FC<JobProgressOverlayProps> = ({
  isVisible,
  progress,
  jobId,
  message = 'Processing your file...',
  errorMessage,
  isComplete = false,
  onCancel,
}) => {
  const { t } = useTranslation();

  if (!isVisible) return null;

  const displayProgress = Math.min(100, Math.max(0, progress));
  const isError = errorMessage !== undefined;

  return (
    <>
      {/* Backdrop - covers full screen */}
      <div className="fixed inset-0 top-[-10%] bg-black/60 z-40" />

      {/* Modal container - centered content */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {isError ? 'Processing Error' : isComplete ? 'Complete' : message}
            </h2>
            {!isError && !isComplete && (
              <p className="text-sm text-gray-500">
                {t('common.processingNote', {
                  defaultValue:
                    'Please wait while your file is being processed...',
                })}
              </p>
            )}
          </div>

          {/* Progress section */}
          {!isError && !isComplete && (
            <div className="space-y-4 mb-6">
              {/* Progress bar with gradient */}
              <div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-sm">
                  <div
                    className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-500 to-blue-600"
                    style={{ width: `${displayProgress}%` }}
                  />
                </div>
              </div>

              {/* Progress percentage and text */}
              <div className="flex items-baseline justify-center gap-0.5">
                <span className="text-5xl font-bold text-blue-600">
                  {displayProgress}
                </span>
                <span className="text-lg font-semibold text-blue-600">%</span>
                <span className="text-sm text-gray-500 ml-2">complete</span>
              </div>
            </div>
          )}

          {/* Complete state */}
          {isComplete && !isError && (
            <div className="mb-6 flex justify-center">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
          )}

          {/* Error state */}
          {isError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm leading-relaxed">
                {errorMessage}
              </p>
            </div>
          )}

          {/* Job ID */}
          {jobId && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
                {t('common.jobId', { defaultValue: 'Job ID' })}
              </p>
              <p className="text-sm font-mono text-gray-700 break-all">
                {jobId}
              </p>
            </div>
          )}

          {/* Action buttons */}
          {(isError || isComplete || onCancel) && (
            <button
              onClick={onCancel}
              className={`w-full px-4 py-2.5 rounded-lg transition-colors font-medium text-sm ${
                isComplete
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              }`}
            >
              {isComplete ? 'Close' : isError ? 'Dismiss' : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </>
  );
};
